import { safeFetch, getBaseUrl } from './api';

// All IPD routes: /ipd/admissions/:admissionId/...
const url = (path: string) => `${getBaseUrl()}${path}`;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IpdVital {
  id: string;
  admission_id: string;
  recorded_at: string;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse: number | null;
  temp_celsius: number | null;
  spo2: number | null;
  respiratory_rate: number | null;
  weight_kg: number | null;
  urine_output_ml: number | null;
  fluid_intake_ml: number | null;
  notes: string | null;
  recorded_by: string;
  recorded_by_name: string;
  created_at: string;
}

export interface AddVitalPayload {
  recorded_at?: string;
  bp_systolic?: number | null;
  bp_diastolic?: number | null;
  pulse?: number | null;
  temp_celsius?: number | null;
  spo2?: number | null;
  respiratory_rate?: number | null;
  weight_kg?: number | null;
  urine_output_ml?: number | null;
  fluid_intake_ml?: number | null;
  notes?: string;
}

// ─── Discharge Types ─────────────────────────────────────────────────────────

export interface DischargeMedicine {
  id?: string;
  drug_id?: number | null;
  drug_name: string;
  drug_type?: string;
  dose?: string;
  taken_when?: string;
  frequency?: string;
  duration?: string;
  notes?: string;
  sort_order?: number;
}

export interface DischargeSummary {
  id: string;
  admission_id: string;
  final_diagnosis: string | null;
  complaints: string | null;
  key_findings_physical: string | null;
  investigation_summary: string | null;
  procedures_done: string | null;
  operative_findings: string | null;
  hospital_course: string | null;
  treatment_given: string | null;
  condition_at_discharge: 'improved' | 'stable' | 'same' | 'deteriorated' | 'expired';
  discharge_advice: string | null;
  additional_medication: string | null;
  others: string | null;
  tests_requested: string | null;
  follow_up_date: string | null;
  follow_up_notes: string | null;
  diet_advice: string | null;
  activity_restriction: string | null;
  acknowledgement: string | null;
  finalized_at: string | null;
  finalized_by: string | null;
  finalized_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DischargePayload extends Omit<DischargeSummary, 'id' | 'admission_id' | 'finalized_at' | 'finalized_by' | 'finalized_by_name' | 'created_at' | 'updated_at'> {
  patient_history?: string;
  medicines: DischargeMedicine[];
  finalize?: boolean;
}

export interface DischargeData {
  admission: any;
  summary: DischargeSummary | null;
  medicines: DischargeMedicine[];
  activeOrders: IpdMedicationOrder[];
}

export interface IpdProgressNote {
  id: string;
  admission_id: string;
  note_text: string;
  noted_at: string;
  noted_by: string;
  noted_by_name: string;
  edited_by: string | null;
  edited_by_name: string | null;
  edited_at: string | null;
  created_at: string;
}

export interface AddNotePayload {
  note_text: string;
  noted_at?: string;
}

export interface IpdMedicationOrder {
  id: string;
  admission_id: string;
  drug_id: number | null;
  drug_name: string;
  generic_name: string | null;
  dose: string;
  route: 'oral' | 'iv' | 'im' | 'sc' | 'topical' | 'inhalation' | 'other';
  frequency: string;
  start_date: string;
  end_date: string | null;
  is_prn: number;
  prn_reason: string | null;
  special_instructions: string | null;
  status: 'active' | 'stopped' | 'completed';
  ordered_by: string;
  ordered_by_name: string;
  stopped_by: string | null;
  stopped_by_name: string | null;
  stopped_at: string | null;
  stop_reason: string | null;
  created_at: string;
}

export interface CreateOrderPayload {
  drug_id?: number | null;
  drug_name: string;
  generic_name?: string;
  dose: string;
  route: string;
  frequency: string;
  start_date: string;
  end_date?: string | null;
  is_prn?: boolean;
  prn_reason?: string;
  special_instructions?: string;
}

// ─── Vitals API ───────────────────────────────────────────────────────────────

export async function getVitals(admissionId: string): Promise<IpdVital[]> {
  return safeFetch<IpdVital[]>(url(`/ipd/admissions/${admissionId}/vitals`));
}

export async function getLatestVital(admissionId: string): Promise<IpdVital | null> {
  return safeFetch<IpdVital | null>(url(`/ipd/admissions/${admissionId}/vitals/latest`));
}

export async function addVital(admissionId: string, data: AddVitalPayload): Promise<IpdVital> {
  return safeFetch<IpdVital>(url(`/ipd/admissions/${admissionId}/vitals`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Ward & Bed API ───────────────────────────────────────────────────────────

export interface Ward {
  id: string; name: string; ward_type: string;
  default_rent_per_day: number;
  is_active: number; notes: string | null;
  bed_count: number; available_beds: number; occupied_beds: number;
}
export interface Bed {
  id: string; ward_id: string; bed_number: string;
  rent_per_day: number | null; status: string; is_active: number;
  notes: string | null;
  current_admission_id: string | null;
  admission_number: string | null;
  current_patient_name: string | null;
  occupancy_status: string;
  effective_rent_per_day: number | null;  // from bed_occupancy view: bed.rent_per_day ?? ward.default_rent_per_day
}

export async function getWards(clinicId: string): Promise<Ward[]> {
  return safeFetch(url(`/ipd/wards?clinic_id=${clinicId}`));
}
export async function createWard(data: Partial<Ward>): Promise<Ward> {
  return safeFetch(url('/ipd/wards'), { method: 'POST', body: JSON.stringify(data) });
}
export async function updateWard(wardId: string, data: Partial<Ward>): Promise<Ward> {
  return safeFetch(url(`/ipd/wards/${wardId}`), { method: 'PATCH', body: JSON.stringify(data) });
}
export async function getBeds(wardId: string): Promise<Bed[]> {
  return safeFetch(url(`/ipd/wards/${wardId}/beds`));
}
export async function createBed(wardId: string, data: Partial<Bed>): Promise<Bed> {
  return safeFetch(url(`/ipd/wards/${wardId}/beds`), { method: 'POST', body: JSON.stringify(data) });
}
export async function updateBed(bedId: string, data: Partial<Bed>): Promise<Bed> {
  return safeFetch(url(`/ipd/beds/${bedId}`), { method: 'PATCH', body: JSON.stringify(data) });
}

// ─── Admit / Charges / Invoice API ───────────────────────────────────────────

export async function createAdmission(data: any): Promise<{ id: string; admission_number: string }> {
  return safeFetch(url('/ipd/admissions'), { method: 'POST', body: JSON.stringify(data) });
}
export async function addCharge(admissionId: string, data: any): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges`), { method: 'POST', body: JSON.stringify(data) });
}
export async function createDischargeInvoice(admissionId: string): Promise<{ invoice_id: string; invoice_number: string; total: number }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/invoice`), { method: 'POST', body: JSON.stringify({}) });
}

// ─── Payments / Ledger API ───────────────────────────────────────────────────

export interface PaymentRecord {
  id: string;
  amount: number | string;
  payment_mode: string;
  reference_number: string | null;
  status: 'success' | 'failed' | 'refunded';
  payment_date: string;
  collected_by: string;
  collected_by_name?: string;
  notes: string | null;
}

export interface AdmissionLedger {
  total_billed: number;
  total_paid: number;
  balance_due: number;
  charges: any[];
  payments: PaymentRecord[];
}

export async function recordPayment(
  admissionId: string,
  data: { amount: number; payment_mode: string; reference_number?: string; notes?: string }
): Promise<PaymentRecord> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/payments`), { method: 'POST', body: JSON.stringify(data) });
}

export async function getLedger(admissionId: string): Promise<AdmissionLedger> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/ledger`));
}

// ─── Room rent summary ────────────────────────────────────────────────────────

export interface RoomRentSummary {
  rate: number;
  total_days: number;
  posted_days: number;
  pending_days: number;
  pending_amount: number;
}

export async function getRoomRentSummary(admissionId: string): Promise<RoomRentSummary> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/room-rent-summary`));
}

export async function postRoomRentPending(admissionId: string): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/room-rent-post`), { method: 'POST' });
}

// ─── Admission API ───────────────────────────────────────────────────────────

export interface AdmissionRow {
  id: string;
  admission_number: string;
  status: string;
  ward_id: string;
  admission_date: string;
  discharge_date: string | null;
  patient_name: string;
  patient_mrn: string;
  admitting_doctor_name: string;
  ward_name: string;
  bed_number: string;
  invoice_number: string | null;
  total_amount: number | null;
  paid_amount: number | null;
  balance_due: number | null;
  available_advance: number | null;
}

export async function getAdmissions(
  clinicId: string,
  opts?: { status?: 'admitted' | 'all'; patientId?: string; dateFrom?: string; dateTo?: string }
): Promise<AdmissionRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.status)    params.set('status', opts.status);
  if (opts?.patientId) params.set('patient_id', opts.patientId);
  if (opts?.dateFrom)  params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)    params.set('date_to', opts.dateTo);
  return safeFetch<AdmissionRow[]>(url(`/ipd/admissions?${params.toString()}`));
}

export async function getAdmission(admissionId: string): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}`));
}

export async function getCharges(admissionId: string, includeCancelled?: boolean): Promise<any[]> {
  const q = includeCancelled ? '?include_cancelled=true' : '';
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges${q}`));
}

export async function cancelCharge(admissionId: string, chargeId: string, cancelReason: string): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges/${chargeId}/cancel`), {
    method: 'PATCH',
    body: JSON.stringify({ cancel_reason: cancelReason }),
  });
}

// ─── Ward / bed transfer API ─────────────────────────────────────────────────

export interface TransferRecord {
  id: string;
  notes: string | null;
  transferred_at: string;
  from_ward_name: string;
  from_bed_number: string;
  to_ward_name: string;
  to_bed_number: string;
  transferred_by_name: string;
}

export async function transferAdmission(
  admissionId: string,
  data: { ward_id: string; bed_id: string; notes?: string }
): Promise<{ id: string; ward_id: string; bed_id: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/transfer`), { method: 'POST', body: JSON.stringify(data) });
}

export async function getTransferHistory(admissionId: string): Promise<TransferRecord[]> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/transfers`));
}

// ─── Admission status API ────────────────────────────────────────────────────

export async function dischargeAdmission(
  admissionId: string,
  data: { status: string; discharge_date: string }
): Promise<{ id: string; status: string; discharge_date: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge`), { method: 'PATCH', body: JSON.stringify(data) });
}

export async function updateAdmissionDates(
  admissionId: string,
  data: { admission_date: string; discharge_date?: string | null }
): Promise<{ id: string; admission_date: string; discharge_date: string | null }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/dates`), { method: 'PATCH', body: JSON.stringify(data) });
}

// ─── Discharge Summary API ───────────────────────────────────────────────────

export async function getDischarge(admissionId: string): Promise<DischargeData> {
  return safeFetch<DischargeData>(url(`/ipd/admissions/${admissionId}/discharge`));
}

export async function saveDischarge(admissionId: string, data: DischargePayload): Promise<{ summary: DischargeSummary; medicines: DischargeMedicine[] }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Progress Notes API ───────────────────────────────────────────────────────

export async function getNotes(admissionId: string, date?: string): Promise<IpdProgressNote[]> {
  const q = date ? `?date=${date}` : '';
  return safeFetch<IpdProgressNote[]>(url(`/ipd/admissions/${admissionId}/notes${q}`));
}

export async function addNote(admissionId: string, data: AddNotePayload): Promise<IpdProgressNote> {
  return safeFetch<IpdProgressNote>(url(`/ipd/admissions/${admissionId}/notes`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNote(admissionId: string, noteId: string, noteText: string): Promise<IpdProgressNote> {
  return safeFetch<IpdProgressNote>(url(`/ipd/admissions/${admissionId}/notes/${noteId}`), {
    method: 'PATCH',
    body: JSON.stringify({ note_text: noteText }),
  });
}

// ─── Medication Orders API ────────────────────────────────────────────────────

export async function getOrders(admissionId: string, statusFilter?: 'all'): Promise<IpdMedicationOrder[]> {
  const q = statusFilter === 'all' ? '?status=all' : '';
  return safeFetch<IpdMedicationOrder[]>(url(`/ipd/admissions/${admissionId}/orders${q}`));
}

export async function createOrder(admissionId: string, data: CreateOrderPayload): Promise<IpdMedicationOrder> {
  return safeFetch<IpdMedicationOrder>(url(`/ipd/admissions/${admissionId}/orders`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function stopOrder(admissionId: string, orderId: string, stopReason: string): Promise<IpdMedicationOrder> {
  return safeFetch<IpdMedicationOrder>(url(`/ipd/admissions/${admissionId}/orders/${orderId}/stop`), {
    method: 'PATCH',
    body: JSON.stringify({ stop_reason: stopReason }),
  });
}
